/**
 * How a stored task value is shown to a person — one dispatcher, two readers.
 *
 * The three labellers below were written for the conflict dialog (§4.5, 5.1)
 * and lived privately inside `stores/tasks.ts`, chosen by hand at each call
 * site. The revision trail (5.4) cannot choose by hand: it renders `before` and
 * `after` for whatever field a revision happens to name, so it needs a lookup
 * keyed by field path — and the moment there are two readers, the labels have
 * to be one declaration.
 *
 * **Not a style preference.** `priority` stores `B`, and `review_assignee_uid`
 * stores a 28-character uid. A trail that printed those raw beside a dialog
 * that prints "High" and a person's name would read as two different fields,
 * and the trail is the surface where a reader is *comparing* values. §4.5's own
 * argument — "a conflict between two 28-character uids is not a choice anyone
 * can make" — applies at least as strongly to an audit row.
 *
 * The field paths are the ones `EditPolicy.i18n_keys` names in
 * `core_models_services`; that map is the source of truth for *which* fields a
 * trail can name, and this is the source of truth for how their values read.
 */
import i18n from '@/plugins/i18n'
import { useMembersStore } from '@/stores/members'
import { memberLabel } from '@/utils/userDisplay'
import type { Member } from '@/models/Member'
import { fmtDateMedium, isoFromTimestamp } from '@/utils/date'
import { rejectionReasonKey } from '@/models/rejectionReason'
import {
  isPriorityKey,
  PRIORITY_URGENCY_KEYS,
  TASK_PRIORITY_TO_KEY,
} from '@/components/shared/priority/tokens'

const t = i18n.global.t

/** The field-path prefix a comment revision names (MYAC-1266). */
const COMMENTS_FIELD = 'comments'

/**
 * A priority as a word, not a letter (§4.5).
 *
 * Handles **both** stored forms on purpose. The picker only ever emits letters,
 * but the value this is asked to label is not always one the picker produced:
 * a conflict reads `stored` straight off the task document, a revision reads it
 * out of the trail, and assessment imports write the word forms (`urgent`,
 * `high`, …) that `TASK_PRIORITY_TO_KEY` exists to map. Labelling only letters
 * would put a raw `urgent` on one side and `HIGH` on the other, which reads as
 * two different kinds of thing rather than two values of one field.
 *
 * `F` is deliberately labelled even though nothing can select it: it is legacy
 * and read-only, so it can be the *stored* side of a conflict, and the `before`
 * side of a revision, while never being the attempted one.
 *
 * Falls back to the raw string rather than "unknown" when it recognises
 * nothing — showing the user what is actually on the task beats telling them we
 * cannot name it.
 */
export function priorityLabel(value: unknown): string {
  if (value == null || value === '') return t('priority.unknown')
  const raw = String(value)
  const key = isPriorityKey(raw) ? raw : TASK_PRIORITY_TO_KEY[raw]
  return key ? t(PRIORITY_URGENCY_KEYS[key]) : raw
}

/**
 * A uid as a person would recognise them — MYAC-1080.
 *
 * **Two lists, project first.** A revision outlives project membership: the
 * trail showed a 28-character uid for someone who had made the change and since
 * left the project, which is the one place in the app where "who did this" is
 * the whole point (R1). Project members take precedence when a uid is in both,
 * matching `UserName.vue`.
 *
 * **The ladder ends here.** There is no third source a browser can reach:
 * `/users/{uid}` is owner-only readable in Firestore, so a client cannot read
 * another person's name that way. For someone removed from the tenant as well
 * as the project, the raw uid is not a degraded answer — it is the only true
 * one left, and it is still an identifier an auditor can act on.
 *
 * That is why this uses `memberLabel` (`display_name → email → uid`) rather
 * than `memberName`, whose contract is "never render a uid" and which exists
 * for pickers and headers where a uid would only look broken. Both are
 * deliberate, and the trail is the surface that wants the former.
 */
export function userLabel(uid: string): string {
  return memberLabel(memberByUid(uid)) || uid
}

/** The member record behind a uid, project first so a uid in both lists resolves to the project record. */
export function memberByUid(uid: string): Member | undefined {
  const store = useMembersStore()
  return (
    store.projectMembers.find((m) => m.uid === uid) ??
    store.tenantMembers.find((m) => m.uid === uid)
  )
}

/**
 * An assignee as a person would recognise them — a name, not a uid (§4.5).
 *
 * `null` is not a fallback case: it is the real, expected value for a cleared
 * assignee, and it gets the same word the picker shows for it.
 *
 * Everything else defers to {@link userLabel} — a revision changing
 * `review_assignee_uid` names the assignee the same way the row names its
 * actor, which was the second half of MYAC-1080.
 */
export function assigneeLabel(value: unknown): string {
  if (value == null || value === '') return t('task.assignee.unassigned')
  return userLabel(String(value))
}

/**
 * A progress value as the board's own column header spells it.
 *
 * Reuses `tasks.columns.*` rather than minting a parallel set, so a trail
 * saying a task moved to "In Progress" names the column the reader can see it
 * sitting in. `cancelled` is legacy and off the board entirely (see
 * `kanbanColumnFor`), so it has no column key and falls through to its raw
 * value rather than being silently renamed.
 */
export function progressLabel(value: unknown): string {
  if (value == null || value === '') return t('task.edit.empty')
  const raw = String(value)
  const key = `tasks.columns.${raw}`
  const label = t(key)
  return label === key ? raw : label
}

/**
 * A review decision in the board's vocabulary (§4.3).
 *
 * `pending` deliberately reads as the *state* ("Proposal un-accepted") rather
 * than the gesture, matching the Inactive column's tag — 5.3 established that
 * "sent back" described what someone did and left the reader guessing what the
 * task now is.
 *
 * Unknown values fall through raw. `TaskReviewDecision` is a `Literal`, not a
 * tolerant enum, so a value from a newer service arrives here unrecognised and
 * showing it beats inventing a name for it.
 */
export function decisionLabel(value: unknown): string {
  if (value == null || value === '') return t('task.edit.empty')
  switch (String(value)) {
    case 'accepted':
      return t('tasks.trail.decision.accepted')
    case 'pending':
      return t('tasks.inactive.sent_back')
    case 'rejected':
      return t('tasks.inactive.rejected')
    default:
      return String(value)
  }
}

/**
 * A content value as it is written (§4.5).
 *
 * The other labellers exist because their stored form is not readable. A
 * finding's stored value *is* what a person reads, so resolving it would only
 * be a chance to change it.
 */
export function textLabel(value: unknown): string {
  if (value == null || value === '') return t('task.edit.empty')
  return String(value)
}

/**
 * A list of documents, as a person reads it — MYAC-1109.
 *
 * `textLabel` would render `[object Object],[object Object]`, and it would do
 * it in the two places where a reader is *comparing* two values: the conflict
 * dialog, whose whole purpose is that the user can choose between them, and
 * the revision trail, which is a compliance artifact.
 *
 * Names only, not the actions. A trail row is a summary — "which documents did
 * this task ask for, before and after" — and a reader who needs the actions has
 * the task itself one click away. Rendering three nested actions per document
 * inside a table cell would bury the change the row exists to show.
 *
 * A tick shows as a ✓ against the name, because since MYAC-1109 the tick IS one
 * of these edits and a revision that rendered two identical name lists for it
 * would be a row saying nothing changed.
 */
export function documentsLabel(value: unknown): string {
  if (!Array.isArray(value) || !value.length) return t('task.edit.empty')
  return value
    .map((d) => {
      const row = d as { name?: unknown; validated?: unknown }
      const name = row?.name == null ? '' : String(row.name)
      return row?.validated ? `✓ ${name}` : name
    })
    .filter(Boolean)
    .join(', ')
}

/**
 * A list of clauses, as they are cited — MYAC-1109.
 *
 * `7.4.1 — Purchasing`, prefixed by the standard when there is one. Same
 * reasoning as {@link documentsLabel}: the optional catalogue `description` is
 * left out because a trail row is a summary, not the clause text.
 */
export function clausesLabel(value: unknown): string {
  if (!Array.isArray(value) || !value.length) return t('task.edit.empty')
  return value
    .map((c) => {
      const row = c as { standard?: unknown; number?: unknown; title?: unknown }
      const cite = [row?.number, row?.title].filter(Boolean).map(String).join(' - ')
      return row?.standard ? `${String(row.standard)} ${cite}` : cite
    })
    .filter(Boolean)
    .join(', ')
}

/**
 * An instant as a date — MYAC-1089's `review_rejected_until`, which a revision
 * stores as a Firestore Timestamp. `textLabel` would print `Timestamp(seconds=…)`.
 */
export function dateLabel(value: unknown): string {
  const iso = isoFromTimestamp(value)
  return iso ? fmtDateMedium(iso) : t('task.edit.empty')
}

/**
 * A set-aside reason by its label — MYAC-1639. A reason this build cannot name
 * reads as an unrecognised value, never as the server's key.
 */
export function rejectionReasonLabel(value: unknown): string {
  if (value == null || value === '') return t('task.edit.empty')
  const key = rejectionReasonKey(value)
  return key ? t(key) : t('tasks.trail.value_unknown')
}

/**
 * Where a task stood — MYAC-1639's `parked → active`, and an archive's move. A
 * lifecycle this build cannot name reads as an unrecognised value.
 */
export function lifecycleLabel(value: unknown): string {
  if (value == null || value === '') return t('task.edit.empty')
  const key = `tasks.trail.lifecycle.${String(value)}`
  const label = t(key)
  return label === key ? t('tasks.trail.value_unknown') : label
}

/**
 * Field path → how its values read.
 *
 * Only fields whose stored form is unreadable appear here; everything else is
 * text and takes {@link textLabel}. Keeping the map small is the point — an
 * entry is a claim that the raw value would confuse someone.
 */
const LABELLERS: Readonly<Record<string, (value: unknown) => string>> = {
  priority: priorityLabel,
  review_assignee_uid: assigneeLabel,
  review_progress: progressLabel,
  review_decision: decisionLabel,
  documents_v2: documentsLabel,
  clauses_v2: clausesLabel,
  review_rejected_until: dateLabel,
  review_rejection_reason: rejectionReasonLabel,
  lifecycle: lifecycleLabel,
}

/**
 * How this field's value reads, whatever the field is.
 *
 * The trail's entry point. Defaults to {@link textLabel} rather than throwing
 * on an unknown path: `EditPolicy` can name a field this build has never heard
 * of, and rendering its raw value is strictly better than rendering nothing.
 */
export function labelForField(field: string, value: unknown): string {
  return (LABELLERS[field] ?? textLabel)(value)
}

/**
 * A stored field path → its `task.fields.*` locale key.
 *
 * `context.finding` → `task.fields.context_finding`. The locale entries mirror
 * `EditPolicy.i18n_keys` in `core_models_services`, which is the source of
 * truth for *which* fields a trail or a conflict can name.
 *
 * **`/\./g`, not `.replace('.', '_')`** — the latter replaces only the first
 * dot. Harmless today, since no policy path has two, and that is exactly why
 * the divergence between the three copies of this line went unnoticed: one of
 * them was already wrong for a path nobody has written yet.
 */
export function fieldKey(field: string): string {
  // A comment names the entry it changed — `comments.{autoId}` (MYAC-1266) —
  // so the id is part of the path, and building a key from it would look up
  // `task.fields.comments_KJ8xn2pQ…`, miss, and print that auto-id at the
  // reader. Every comment row reads the same way instead: the thread already
  // says WHICH comment by sitting next to it, and the trail row carries the
  // before and after text, which is the part an audit reader came for.
  if (field === COMMENTS_FIELD || field.startsWith(`${COMMENTS_FIELD}.`)) {
    return 'task.fields.comment'
  }
  return `task.fields.${field.replace(/\./g, '_')}`
}

/**
 * A field's name as a person reads it, falling back to the raw path.
 *
 * Third copy of this, consolidated on review of #472 — the trail, the conflict
 * dialog and the new-task dialog each built the key themselves, and two of the
 * three had drifted apart. It is the PR's own argument about the value
 * labellers applied one function further: the moment two surfaces name the same
 * field, the naming has to be one declaration.
 *
 * Falls back to the raw path rather than an empty string: an unlabelled field
 * is a missing translation, and showing `context.finding` is worse-looking but
 * still answers "which field is this".
 */
export function fieldLabel(field: string): string {
  // The `t(key) === key` sentinel rather than `te(key)`. The conflict dialog
  // used `te` from `useI18n()`, whose component scope resolves through the
  // locale fallback chain; `i18n.global.te` does not, so it reports every key
  // missing when the active locale is a region variant like `en-US` and the
  // messages sit under `en`. The sentinel is what `progressLabel` and the
  // trail's own entry labeller already use, and it survives that.
  const key = fieldKey(field)
  const label = t(key)
  return label === key ? field : label
}
